import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ArrowLeft,
  Home,
  MessageCircle,
  ShoppingCart,
  User,
  Bell,
  Store,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);

  const navigate = useNavigate();
  const { cartItems, addToCart } = useCart();
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const dropdownRef = useRef();

  // Fetch stores and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const storeSnap = await getDocs(collection(db, "Stores"));
        const storeData = storeSnap.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setStores(storeData);

        const productSnap = await getDocs(collection(db, "products"));
        const productData = productSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          images: doc.data().images || [],
        }));
        setProducts(productData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter products
  const filteredProducts = useMemo(() => {
    const term = searchQuery.toLowerCase();
    return products.filter((p) => {
      const matchesSearch = p.name?.toLowerCase().includes(term);
      const matchesStore =
        !selectedStore || p.storeId === selectedStore.id;
      return matchesSearch && matchesStore;
    });
  }, [products, searchQuery, selectedStore]);

  const handleBack = () => navigate(-1);

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      image: product.images[0] || product.image,
      quantity: 1,
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowStoreDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="flex items-center p-3">
          <button
            onClick={handleBack}
            className="p-0 rounded-full hover:bg-gray-100 transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>

          <div className="flex flex-1 ml-3 max-w-3xl w-full items-center space-x-2">
            {/* Search */}
            <form className="relative flex-1 w-full">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-10 rounded-[10px] border border-blue-600 focus:outline-none bg-white text-gray-800"
              />
              <Search className="absolute left-4 top-3.5 w-5 h-5 text-gray-500" />
            </form>

            {/* Shop Icon Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowStoreDropdown((prev) => !prev)}
                className="bg-blue-600 p-2 rounded-full text-white flex items-center justify-center"
              >
                <Store className="w-5 h-5" />
              </button>

              {showStoreDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-300 rounded shadow-lg z-50 max-h-60 overflow-y-auto">
                  <button
                    className={`w-full text-left px-3 py-2 hover:bg-blue-100 ${
                      !selectedStore ? "font-bold" : ""
                    }`}
                    onClick={() => {
                      setSelectedStore(null);
                      setShowStoreDropdown(false);
                    }}
                  >
                    All Stores
                  </button>
                  {stores.map((store) => (
                    <button
                      key={store.id}
                      className={`w-full text-left px-3 py-2 hover:bg-blue-100 ${
                        selectedStore?.id === store.id ? "font-bold" : ""
                      }`}
                      onClick={() => {
                        setSelectedStore(store);
                        setShowStoreDropdown(false);
                      }}
                    >
                      {store.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {loading ? (
          <p className="col-span-full text-center text-gray-600">Loading...</p>
        ) : filteredProducts.length > 0 ? (
          filteredProducts.map((product) => {
            const productImage =
              product.images[0] || product.image || "https://via.placeholder.com/300";
            const originalPrice = (product.price * 1.3).toFixed(2);

            return (
              <div
                key={product.id}
                className="bg-white p-1 rounded-lg shadow hover:shadow-lg transition flex flex-col relative cursor-pointer"
              >
                <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 text-xs font-semibold rounded">
                  30% OFF
                </div>

                <img
                  src={productImage}
                  alt={product.name}
                  className="w-full h-40 object-cover rounded mb-2 bg-[#e5e5e5]"
                  onClick={() => navigate(`/product/${product.id}`)}
                />

                <h2 className="font-semibold text-gray-800 text-sm md:text-lg">
                  {product.name}
                </h2>

                <p className="text-gray-600 text-xs md:text-sm line-clamp-2">
                  {product.description}
                </p>

                <div className="flex items-center mt-1 gap-2">
                  <span className="text-indigo-600 font-bold text-sm">
                    ₹ {product.price}
                  </span>
                  <span className="text-gray-400 line-through text-xs">
                    ₹ {originalPrice}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <p className="col-span-full text-center text-gray-600 mt-4">
            No products found
          </p>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 w-full bg-white shadow-[0_-4px_10px_rgba(0,0,0,0.15)] h-14 md:hidden flex justify-around items-center z-20">
        <button
          onClick={() => navigate("/")}
          className="flex items-center justify-center w-24 h-10 bg-yellow-400 rounded-xl text-black gap-2"
        >
          <Home className="w-5 h-5" />
          <span className="text-sm">Home</span>
        </button>

        <button onClick={() => navigate("/messages")}>
          <MessageCircle className="w-5 h-5" />
        </button>

        <button onClick={() => navigate("/notifications")}>
          <Bell className="w-5 h-5" />
        </button>

        <button onClick={() => navigate("/cart")} className="relative">
          <ShoppingCart className="w-5 h-5" />
          {totalItems > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-black text-xs px-1 rounded-full">
              {totalItems}
            </span>
          )}
        </button>

        <button onClick={() => navigate("/profile")}>
          <User className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default SearchPage;
